import Backbone from 'backbone'
import WidgetView from '../widget-view'
import DatasetTableWidgetConfig from './dataset-table-widget-config'
import _ from 'lodash'
import template from './dataset-table-widget-view.hbs'
import globals from 'scoreboard/components/globals'
import layoutManager from 'scoreboard/components/layout-manager'
import LoadingDots from 'scoreboard/components/loading-dots'
import unwrapModel from 'scoreboard/components/unwrap-model'
import DatasetsTabularResult, { getAggregationSortField } from 'scoreboard/datasets/overview/datasets-tabular-result'
import DatasetsValueResult from 'scoreboard/datasets/overview/datasets-value-result'
import {DATASET_FETCH_PROPERTIES} from '../maestro'
import TableSettings from 'scoreboard/datasets/overview/table-settings'
import ChooseColumns from 'scoreboard/datasets/overview/choose-columns'
import i18n from 'scoreboard/components/i18n'
import center from 'scoreboard/components/min-height-vertical-center-msg.hbs'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import nonAbbreviatedValuesTooltip from 'scoreboard/components/non-abbreviated-values-tooltip.hbs'
import applyFilter from 'scoreboard/datasets/apply-filter'
import calendars from 'scoreboard/components/calendars'
import cloneWithoutResponseDataset from 'scoreboard/datasets/clone-without-response-dataset'
import {fieldNotReady} from '../dataset-widget-not-ready'
import togglePrefixedClass from 'scoreboard/components/toggle-prefixed-class'
import handleDatasetWidgetError from 'scoreboard/dashboards/widgets/handle-dataset-widget-error'
import Dataset from 'scoreboard/datasets/model/dataset'
import applyDrilldownActionFactory from '../apply-drilldown-action-factory'

const DatasetValueWidgetView = WidgetView.extend({
	className: 'widget dataset-table',
	resizable: true,
	lockedAR: false,

	nonFetchProperties: WidgetView.prototype.nonFetchProperties
		// don't need to do dashboard data fetch for these properties because they will actually trigger a fetch
		// directly to the dataset API endpoints
		.concat(DATASET_FETCH_PROPERTIES),

	initialize: function () {
		WidgetView.prototype.initialize.apply(this, arguments)
		this.listenTo(this.model.properties, 'change:label', this.updateHeader.bind(this))

		// A "breadcrumb" list of drilldown filters that have been applied to this table;
		// the last item is the most recent drilldown filter
		this.drilldownFilters = []

		if (this.model.initialState) {
			if (this.model.initialState.drilldownFilters) {
				this.drilldownFilters = this.model.initialState.drilldownFilters
			}

			this.filters(this.model.initialState.currentFilters)

			if (this.model.initialState.currentSort) {
				let table = _.cloneDeep(this.table())
				const sortField = this.model.initialState.currentSort.sortField
				const ascending = this.model.initialState.currentSort.ascending
				table.set({ sortField, ascending })

				this.table(table)
			}
		}
	},

	render: function () {
		WidgetView.prototype.render.apply(this, arguments)

		if (this.$('.content').length === 0) {
			this.$el.append(template())
			this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container')[0] })
			// Hide the .empty-state-container until it's needed (if needed), because it being there
			// during the initial render can cause scrollbars where there shouldn't be any #23295.
			this.$('.empty-state-container').addClass('is-not-displayed')
		}

		togglePrefixedClass(this.el, 'font-size', this.model.properties.get('fontSize'))

		if (_.isNil(this.datasetId())) {
			// #58815 Data Source is set to "None", so just give a message
			// that a dataset needs to be chosen.
			// Need to use onPaint() to make sure canvas is done drawing the
			// widgets, so WidgetEmptyState can size itself properly
			this.canvas.onPaint(() => {
				this.error({ messageCode: 'common.error.mustSelect.dataset' })
			})
			this.readyForDisplayAndExport()
			return this
		}

		// The dimensions are fixed, so it's safe to display this now with loading dots. But we don't want the export
		// to run until the results are displayed
		this.readyForDisplay()
		this.setOverflowState()

		if (this.dataset()) {
			// #30860 if dataset metadata isn't loaded on initial widget render
			// (like when undoing a deletion), sorting won't work. But we don't
			// have any data to display anyway, so just skip it
			this.sortRows()
		}

		return this
	},

	resize() {
		// Called by this.model.wob 'resize' event listener in widget-view
		this.setOverflowState()
	},

	setOverflowState() {
		let panelContent = this.$('.panel-content')
		let hasContent = panelContent.length > 0

		// When the dataset widget is resized, append the .is-overflowing class
		// if the content within the widget is overflowing (it scrolls). This
		// is used to correctly preserve overflow as we go from the dashboard edit
		// fullscreen mode and back #23295
		// Append .is-overflowing-y when the widget is overflowing vertically so that we can
		// nix the bottom border to avoid a double bottom border with .content
		// Append .is-overflowing-x so that we can use it for bordering when the widget is displayed in export mode
		let overflowing = this.isOverflowing(panelContent)
		let overflowY = this.isOverflowingY(panelContent)
		let overflowX = this.isOverflowingX(panelContent)
		this.$el.toggleClass('is-overflowing', hasContent && overflowing)
		this.$el.toggleClass('is-overflowing-y', hasContent && overflowY)
		this.$el.toggleClass('is-overflowing-x', hasContent && overflowX)
	},

	clearLoadingDots() {
		this.loadingDots.clear()
		this.resultView && this.resultView.remove()
		this.$el.removeClass('is-loading')
	},

	showResults(show) {
		this.$('.results-container').toggleClass('is-not-displayed', !show)
		this.$('.empty-state-container').toggleClass('is-not-displayed', show)
	},

	/**
	 * Renders the error/empty state for the widget.
	 *
	 * @param {ResolvableExceptionResponse} errorResponse The response from the server
	 * containing the error. Object expected to include properties like
	 * `messageCode`, `arguments`, and `stackTrace`
	 */
	error(errorResponse) {
		handleDatasetWidgetError(this, this.dataset(), this.container, 'table', errorResponse)
		this.setOverflowState()
	},

	renderResult (request, response, result) {
		this.clearLoadingDots()

		let dataset = this.dataset()
		let table = this.table()
		let field = dataset.combinedFieldsById[table.get('fieldId')]

		if (!fieldNotReady(dataset, field) && !response?.queryError) {
			this._removeEmptyState()
			this.showResults(true)

			let underlying = dataset.getUnderlyingField(field)
			let aggregatedResult = TableSettings.aggregatedResult(underlying, table)

			if (aggregatedResult && _.isNil(result) && response.totalError) {
				// if there is an error with the the "total" query (see #19004),
				// and the table widget settings show a single result (see #19048),
				// the table's results may be null. In this scenario, it is appropriate
				// to show the error message for the "total" field
				this.error(response.totalError)
				return
			}

			let treeItemsById = _.keyBy(response.treeItems, 'id')

			// only pass in filters related to this field; they are used to render
			// the drilldown header (e.g. FORSCOM and the Back button)
			// #17674 use all filters that were sent with the request, not just the manual filters
			let filters = _.filter(request.filters, f => {
				return f.fieldId == field?.id && _.isNil(f?.aggregationId)
			})

			let ResultView = aggregatedResult ? DatasetsValueResult : DatasetsTabularResult

			ResultView = ResultView.extend({
				headerLabel: () => this.model.properties.get('label')
			})

			let filtersByLinkGroup = _(filters)
				.filter((filter) => !_.isNil(filter.operator))
				.groupBy('linkGroup')
				.value()

			this.resultView = new ResultView({
				model: dataset,
				field: field,
				table,
				allFilters: filters,
				result: result,
				treeItemsById: treeItemsById,
				blank: result.blank,
				numResults: response.numResults,
				request,
				response,
				// if we're in edit mode, we don't want the view to show non abbreviated tooltips
				suppressTooltips: !this.$el.parent().hasClass('read-only'),
				filtersByLinkGroup
			})

			this.$('.results-container')
				.removeClass('widget-empty-state')
				// Prepend so content is before no-results message and so no-results message remains on re-render.
				.prepend(this.resultView.el)
				this.resultView.render()

			if (_.isNil(this.noResultsMessage)) {
				this.noResultsMessage = true
				let panelHeaderHeight = this.$('.panel table thead').height() + 10 // 10 for padding
				this.$('.no-results-message')
					.append(center(i18n.get('dashboards.datasets.noMatching', dataset.attributes.tracked)))
					.css('height', `calc(100% - ${panelHeaderHeight}px)`)
			}

			if (!this.disabled()) {
				this.resultView.on('drilldown', (filter, value) => this.trigger('drilldown', filter, value))
				this.resultView.on('back', (filter, result) => this.trigger('back', filter, result))
				this.resultView.on('click-value-cell', this.navigateDrilldown.bind(this))
			}

			// If we're in the read-only state (dashboard-overview) and if table values are abbreviated
			// display non-rounded tooltips on hover
			if (this.$el.parent().hasClass('read-only') && _.get(table, 'abbreviateNumbers', true)) {
				this.nonAbbreviatedTooltip = new TooltipAndDetails({
					el: this.el,
					selector: '.value.is-shortened',
					tooltipContent: (e) => {
						return nonAbbreviatedValuesTooltip({
							header: e.target.dataset.field,
							value: e.target.dataset.unshortenedValue
						})
					},
					tooltipOptions: {
						extraClass: 'tooltip-and-details non-abbreviated-tooltip datasets-overview-non-abbreviated-tooltip'
					}
				})
			}
		} else {
			this.error()
		}

		this.setOverflowState()
		this.readyForExport()
	},

	navigateDrilldown (targetEl, datasetSeriesOptions) {
		let lastRequestFilters = this.resultView.options.request.filters

		let filters, table
		if (datasetSeriesOptions.value && !_.isNil(datasetSeriesOptions.value.value)) {
			// If there was a value on the click, apply a drilldown to the filters
			// used in the last dataset request, using the clicked value
			let dataset = this.dataset()
			let field = dataset.getUnderlyingField(this.table().get('fieldId'))

			// We clone 'table' because applyFilter() modifies it, and we want
			// to pass those modifications along
			table = _.cloneDeep(this.table())

			filters = applyFilter(field,
				lastRequestFilters,
				table,
				datasetSeriesOptions.value.value)
		} else {
			// If no value, then user had probably drilled down to a single value
			// already, so the filters used in the last dataset request are fine
			filters = lastRequestFilters
			table = this.table()
		}

		// Convert any period deltas to period ids (datasets section doesn't use deltas)
		filters = filters
			.map(raw => {
				let f = _.cloneDeep(raw)

				let convertToAbsolute = (index) => {
					let deltaProp = 'periodDelta' + index
					let absoluteProp = 'periodId' + index

					let delta = f[deltaProp]

					if (!_.isNil(delta)) {
						let currentPeriodId
						if (_.isNil(f.calendarId)) {
							// If the filter's calendarId is null, we're using
							// the global calendar
							currentPeriodId = this.model.options.getPeriodId()
						} else {
							// #47719 if the filter has a calendarId, then we
							// need to translate the global calendar period
							// to the filter's specified calendar
							currentPeriodId = calendars.correspondingPeriod(
								f.calendarId, this.model.options.getPeriodId()).id
						}

						let period = calendars.relativePeriod(currentPeriodId, delta)
						f[absoluteProp] = period.id
						delete f[deltaProp]
					}
				}

				convertToAbsolute(1)
				convertToAbsolute(2)

				return f
			})

		let request = {
			id: this.datasetId(),
			aggregationType: datasetSeriesOptions.columnAggregationType,
			aggregationFieldId: datasetSeriesOptions.columnFieldId,
			filters: filters,
			tables: [ unwrapModel(table) ]
		}

		Backbone.trigger(Dataset.DRILLDOWN, this.datasetId(), { request })
	},

	updateHeader: function () {
		this.resultView && this.resultView.updateHeaderLabel()
	},

	sortRows: function() {
		const table = this.table()
		// make sure there is both a field to sort by AND a sort order
		// AND we have a sortable results view
		if (this.resultView && this.resultView.sortableTable) {
			let isSortValid = _.bind(this.resultView._isSortFieldValid || _.stubTrue, this.resultView)
			const sortField = table.get('sortField')
			if (isSortValid(sortField)) {
				this.resultView.sortableTable.sort(sortField, table.get('ascending'))
			}
		}
	},

	_createConfigView: function () {
		let datasetOptions
		if (this.resultView && this.resultView.options) {
			datasetOptions = cloneWithoutResponseDataset(this.resultView.options)

			if (datasetOptions.table) {
				if (this.resultView.sortableTable) {
					datasetOptions.table.sortable = true
					// add the aggregations field coordinates e.g. "0,1" to each
					// so we can sort when they is selected in the config menu
					const currentAggregations = datasetOptions.table.get('aggregations')
					const aggregationsMetadata = this.resultView.aggregationsMetadata(currentAggregations)
					const aggregations = _.map(currentAggregations, (agg, i) => {
							const metadata = aggregationsMetadata[i]
							const extraProps = {
								sortField: getAggregationSortField(
									metadata.coordinates)
							}
							return _.extend(agg, extraProps)
						})
					datasetOptions.table.set({aggregations})
				} else {
					datasetOptions.table.sortable = false
				}
			}
		}
		return new DatasetTableWidgetConfig({
			model: this.model,
			canvas: this.canvas,
			datasetOptions,
			widgetUpdater: this.widgetUpdater
		})
	},

	datasetId () {
		return this.model.datasetId()
	},

	/**
	 * @return {Dataset}
	 */
	dataset () {
		return this.model.data.datasetModel(this.datasetId())
	},

	loading () {
		this.loadingDots.render()
		this.$el.addClass('is-loading')
	},

	/**
	 * @returns {DatasetTable}
	 */
	table (table) {
		if (arguments.length === 0) {
			let table = _.cloneDeep(this.currentTable || this.model.table)

			if (_.isEmpty(table.get('aggregations'))) {
				// #16231 if the default aggregations are used (ie table.aggregations is null),
				// we need to fill in the default aggregations, or the aggregation settings for
				// dataset value widgets will interfere with the table columns.
				// Note that we do this here instead of in dataset-table-widget-model, because
				// the config view should still see a null table.aggregations and treat it as
				// "use the default"
				const aggregations = _.cloneDeep(ChooseColumns.aggregations(this.dataset(), table))
				table.set({aggregations})
				table.isDefaultAggregations = true
			}

			return table
		}

		this.currentTable = table
	},

	filters (filters) {
		if (arguments.length === 0) {
			// Note: unlike in dataset-filter-widget-view, there's no need to assign
			// an linkGroup here, because:
			// - manual filters will either not have them, or they will be remapped later
			//   in maestro
			// - drilldown filters have them assigned below in _doDrilldown()
			return _.cloneDeep(this.currentFilters || this.model.filters() || [])
		}

		this.currentFilters = filters
	},

	/**
	 * Applys drilldown logic to the table's filters. Returns the filters that should
	 * be shared with other dashboards
	 */
	datasetDrilldown (table, value, drilldownActionId) {
		let sharedFilters = this._doDrilldown(table, value, drilldownActionId, applyFilter)
		this.drilldownFilters.push(sharedFilters[0])
		return sharedFilters
	},

	/**
	 * Applys drilldown "back" logic to the table's filters. Returns the filters that should
	 * be shared with other dashboards
	 */
	datasetBack (table, result, drilldownActionId) {
		let sharedFilters = this._doDrilldown(table, result, drilldownActionId, (field, filters, table, value) => {
			if (filters.length === 0) {
				// If drilldownFilters is empty, but Back was available, there must have been manual
				// filters showing. So pass those along to back() so they can be walked back, too
				filters = this.model.filters() ?? []

				if (filters.length === 0) {
					// #50015 the "back" logic expects there to be a filter, but
					// there may not be one if the "back" action came from a
					// map chart. So just construct a fake filter at the root
					// of the rollup tree (which is effectively the same as no
					// filter) and send that to the "back" logic
					filters = [buildRootFilter({ dataset: this.dataset(), table })]
				}
			}
			return applyFilter.back(field, filters, table, value)
		})

		// Remove the previous drilldown filter that we just went "back" from
		this.drilldownFilters.splice(this.drilldownFilters.length-1, 1)

		if (sharedFilters[0] && sharedFilters[0].rollupIsAnyFilters) {
			// #18612 If we're walking back up a rollup tree, the last drilldown filter
			// might not actually be where we went back to.
			// For example, user could have drilled down to a grandchild, then just went
			// back up 1 level to the child they skipped over.
			// To handle this, replace the last drilldown filter with the one with the
			// proper "back" logic applied.
			this.drilldownFilters.splice(this.drilldownFilters.length-1, 1)
			this.drilldownFilters.push(sharedFilters[0])
		} else if (_.isEmpty(sharedFilters)) {
			// If sharedFilters is empty, then we're at the root, so there's
			// no drilldown anymore
			this.drilldownFilters = []
		}

		return sharedFilters
	},

	/**
	 * Applys drilldown logic directly using the given filter. Used when the
	 * drilldown comes from directly editing the filter in the filter drawer,
	 * instead of a click on the a table row or the Back button
	 *
	 * @param {ApiDatasetFilter} [drilldownFilter] the filter to use; if null,
	 * the drilldown state will be removed (ie go back to root)
	 */
	datasetDirectDrilldown (drilldownFilter) {
		if (drilldownFilter) {
			this.drilldownFilters.push(drilldownFilter)
		} else {
			this.drilldownFilters = []
		}

		return this._doDrilldown(
			this.table(),
			// 'value' not necessary because we're not using applyFilter()
			null,
			// drilldownActionId not used when directly applying a filter
			null,
			() => drilldownFilter ? [ drilldownFilter ] : [])
	},

	_doDrilldown (table, value, drilldownActionId, applyFn) {
		let dataset = this.dataset()
		let field = dataset.getUnderlyingField(this.table().get('fieldId'))
		// Note: cloning the previous filter because applyFn() may mutate it
		let currentDrilldownFilter = _.cloneDeep(_.last(this.drilldownFilters))

		let applyDrilldownAction = applyDrilldownActionFactory({
			widgetView: this,
			drilldownActionId
		})

		// When applying drilldown/back to the table filters, we only want to work with and return
		// the filters that would be shared with other widgets; any manually configured filters should
		// not be shared with other widgets.
		// Also note that we clone 'table' because applyFilter() modifies it, so we can't use the
		// same 'table' object twice.
		let sharedFilters = applyFn(field,
			currentDrilldownFilter ? [ currentDrilldownFilter ] : [],
			_.cloneDeep(table), value)

		sharedFilters.forEach(applyDrilldownAction)

		let [sameFieldFilters, otherFieldFilters] =
			_.partition(this.filters(), f => {
				return dataset.getUnderlyingField(f.fieldId)?.id === field.id
			})

		// Now apply drilldown/back to the full list of filters this widget has,
		// which includes manually configured filters (#60895 as long as they
		// are on the same field)
		let combinedFilters = applyFn(field, sameFieldFilters, table, value)
		combinedFilters.forEach(applyDrilldownAction)

		if (combinedFilters.length > 0 && sharedFilters.length > 0) {
			// The drilldown filter that we're returning as a sharedFilter needs a cid.
			// The first (and only) sharedFilter is the same as the first (of possibly many)
			// combinedFilters, so we can safely assign them the same cid
			combinedFilters[0].cid = sharedFilters[0].cid
		}

		// Now add back in the manual filters that were on a different field
		otherFieldFilters.forEach(f => combinedFilters.push(f))

		this.filters(combinedFilters)

		// #18504 apply-filter may modify the table, so make sure we keep track of
		// those changes so that they're used when doing the query
		this.table(table)

		return sharedFilters
	},

	/**
	 * Returns whether a click event on the given element should trigger
	 * a widget-level drilldown to another page. Note this is NOT the same as
	 * the drilldown that updates the dashboard's dataset filters.
	 *
	 * @override
	 */
	isDrilldownExcluded: function(evtEl) {
		return this.elIsOrIsDescendantOfSelector(evtEl,
			'.is-sortable, .drilldown, .value')
	},

	/**
	 * @override
	 */
	disabled: function () {
		if (WidgetView.prototype.disabled.apply(this, arguments)) {
			return true
		}

		if (layoutManager.sharedResource) {
			return true
		}

		return false
	},

	/**
	 * @override
	 */
	getState: function () {
		return {
			drilldownFilters: this.drilldownFilters,
			currentFilters: this.currentFilters,
			currentSort: _.get(this.resultView, 'currentSort')
		}
	},

	drilldownFieldAndId () {
		// Get the most recent drilldown filter on this table
		let currentDrilldownFilter = _.last(this.drilldownFilters)
		if (!currentDrilldownFilter) {
			return null
		}

		// Use the first rollup tree ID on the filter (there really should only be one)
		let drilldownId = _(currentDrilldownFilter.rollupIsAnyFilters).keys().first()
		if (!drilldownId) {
			return null
		}

		let fieldId = this.table().get('fieldId')

		return { drilldownId, fieldId }
	},

	_removeEmptyState() {
		this.widgetEmptyState && this.widgetEmptyState.remove()
	},

	remove: function () {
		WidgetView.prototype.remove.apply(this, arguments)
		_.invokeMap([this.loadingDots, this.resultView, this.nonAbbreviatedTooltip], 'remove')
		this._removeEmptyState()
	}
})

function buildRootFilter ({ dataset, table }) {
	let tableField = dataset.getUnderlyingField(unwrapModel(table).fieldId)
	return {
		fieldId: tableField.id,
		operator: 'is-any',
		rollupIsAnyFilters: {
			[tableField.treeId]: 'item-rollups-and-descendants'
		}
	}
}

// from dataset-value.scss
const titleHeight = 43

export default DatasetValueWidgetView

DatasetValueWidgetView.__filename__ = 'dashboards/widgets/dataset-table/dataset-table-widget-view.js'
